import type React from "react";
import {
  Pagination,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  type SelectChangeEvent,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import type { PaginationParam } from "../../types/paginationResponse";

type TranslationKeys = keyof typeof translations.en;

interface TablePaginationProps {
  count: number;
  pagination: PaginationParam;
  onPaginationChange: (pagination: PaginationParam) => void;
  rowsPerPageOptions?: number[];
  showRowsPerPage?: boolean;
}

const TablePagination: React.FC<TablePaginationProps> = ({
  count,
  pagination,
  onPaginationChange,
  rowsPerPageOptions = [5, 10, 20, 50, 100],
  showRowsPerPage = false,
}) => {
  const { t } = useTranslation();

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    onPaginationChange({
      ...pagination,
      page: value,
    });
  };

  const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
    onPaginationChange({
      ...pagination,
      page: 1, // Reset to first page when changing rows per page
      pageSize: event.target.value as number,
    });
  };

  const startItem = (pagination.page - 1) * pagination.pageSize + 1;
  const endItem = Math.min(pagination.page * pagination.pageSize, count);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 3, mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Pagination
          count={Math.ceil(count / pagination.pageSize)}
          page={pagination.page}
          onChange={handlePageChange}
          color="primary"
          showFirstButton
          showLastButton
        />
        {showRowsPerPage && (
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="rows-per-page-label">{t("Rows per page")}</InputLabel>
            <Select
              labelId="rows-per-page-label"
              value={pagination.pageSize}
              label={t("Rows per page")}
              onChange={handleRowsPerPageChange}
              size="small"
            >
              {rowsPerPageOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
        {t("Showing {{start}}-{{end}} of {{total}} items", {
          start: startItem,
          end: endItem,
          total: count,
        })}
      </Typography>
    </Box>
  );
};

export default TablePagination;
